import { ThemeProvider } from "@mui/system";
import { useEffect, useLayoutEffect, useState } from "react";
import { Provider } from "react-redux";
import { theme } from "./Customization/Theme";
import useVersionCheck from "./Hooks/useVersionCheck";
import store, { useAppSelector } from "./Store";
import { useAuthStore } from "./Store/Slices/AuthSlice";
import ThemedApp from "./ThemedApp";
import { validateToken } from "./Utils/Utils";
import {
  AnimationStyles,
  ButtonContainer,
  CancelButton,
  ContentWrapper,
  IconCircle,
  Overlay,
  TextContainer,
  TitleText,
  UpdateButton,
  VersionPopupContainer,
  VersionText,
} from "./Utils/VersionPopupStyles";
import { localStoragePersister, queryClient } from "./Utils/tanstack-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { defaultShouldDehydrateQuery } from "@tanstack/react-query";
import useProduktlyUser from "./Hooks/useProduktlyUser";
import AppModel from "./Hooks/useModel/AppModel";
import AppButton from "./AppComponents/AppButton";
import { AppConfig } from "./Config/app-config";
import * as Sentry from "@sentry/react";

validateToken();

if (window.location.origin === "https://app.wabo.ai/") {
  Sentry.init({
    dsn: "https://c38cc9c1273082c611f3ec8baf54831b@o4507446172188672.ingest.us.sentry.io/4507447376019456",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    environment: process.env.REACT_APP_SENTRY_ENV,
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ["localhost"],
    tracePropagationTargets: [process.env.REACT_APP_SENTRY_DOMAIN as string],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

interface AnimatedUpdateNotificationProps {
  isUpdateAvailable: any;
  currentVersion: any;
  latestVersion: any;
  updateApp: () => any;
  dismissPopup: any;
}

const AnimatedUpdateNotification: React.FC<AnimatedUpdateNotificationProps> = ({
  isUpdateAvailable,
  currentVersion,
  latestVersion,
  updateApp,
  dismissPopup,
}) => {
  const [animationState, setAnimationState] = useState("hidden");
  const [dismissed, setDismissed] = useState(false);
  const { userConfig } = useAppSelector(useAuthStore);
  useLayoutEffect(() => {
    const data = AppConfig.getLoggedInData();
    const workspaceId = data?.data?.user?.workspace_id;
    if (workspaceId) {
      localStorage.setItem("ws_id", workspaceId);
    } else {
      console.log("Workspace ID not found in logged in data");
    }
  }, []); 
  useEffect(() => {
    if (isUpdateAvailable) {
      setAnimationState("entering");
      const timer = setTimeout(() => {
        setAnimationState("stable");
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setAnimationState("hidden");
    }
  }, [isUpdateAvailable]);

  // Hiding the version update popup when these conditions occurs......
  if (animationState === "hidden") return null;

  return (
    <>
      <Overlay animationState={animationState} />
      <VersionPopupContainer animationState={animationState}>
        <ContentWrapper>
          <IconCircle>🎉</IconCircle>

          <TextContainer>
            <TitleText>A new version is available!</TitleText>
            <VersionText>
              Current: {currentVersion} → Latest: {latestVersion}
            </VersionText>
          </TextContainer>
        </ContentWrapper>

        <ButtonContainer>
          <CancelButton onClick={dismissPopup}>Cancel</CancelButton>
          <UpdateButton onClick={updateApp}>Update Now</UpdateButton>
        </ButtonContainer>
      </VersionPopupContainer>

      <AnimationStyles />
    </>
  );
};

const AppDisabledFBM = (): JSX.Element => {
  const { userConfig } = useAppSelector(useAuthStore);

  // Use useEffect to update state when userConfig changes
  const [showModal, setShowModal] = useState(
    userConfig?.payload?.whatsapp_enabled
  );

  useEffect(() => {
    // Show modal if userConfig exists and WhatsApp is disabled
    if (
      userConfig &&
      userConfig.payload &&
      userConfig.payload.whatsapp_enabled === false
    ) {
      setShowModal(true);
    }
  }, [userConfig]);

  const dismissPopup = () => {
    setShowModal(false);
  };

  const animationState = "stable"; // Changed from "hidden" to "stable" to show the modal

  return (
    <AppModel isShowing={showModal} onClose={dismissPopup}>
      <Overlay animationState={animationState} />
      <VersionPopupContainer
        animationState={animationState}
        height="500px"
        width="500px"
      >
        <ContentWrapper>
          <IconCircle>🚫</IconCircle>
          <TextContainer>
            <TitleText>
              Your WhatsApp Business Number Has Been Disabled by Meta
            </TitleText>
            <VersionText>
              Unfortunately, your WhatsApp number appears to be disabled or
              restricted by Meta, which means you're currently unable to send or
              receive messages. But don’t worry — you can submit a review to
              request reinstatement directly from your WhatsApp Business
              Manager.
            </VersionText>
          </TextContainer>
          <a
            href="https://help.wabo.ai/getting-started/how-to-re-enable-a-phone-number-disabled-by-meta-on-whatsapp-business"
            target="_blank"
            rel="noopener noreferrer"
          >
            Steps to Re-enable a Disabled WhatsApp Number
          </a>
        </ContentWrapper>

        <div
          style={{
            width: "100%",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            justifyContent: "flex-end",
            marginTop: "1rem",
          }}
        >
          <AppButton
            variant="outline"
            buttonStyle={{ width: "100px" }}
            onClick={() => {
              dismissPopup();
            }}
          >
            Close
          </AppButton>

          <AppButton
            variant="primary"
            buttonStyle={{ width: "200px" }}
            onClick={() => {
              window.open(
                "https://business.facebook.com/settings/whatsapp/review",
                "_blank"
              );
              dismissPopup();
            }}
          >
            Submit Review to META
          </AppButton>
        </div>
      </VersionPopupContainer>

      <AnimationStyles />
    </AppModel>
  );
};

const AppContent = () => {
  const { userConfig } = useAppSelector(useAuthStore);
  const isAuthenticated = userConfig !== null;
  const isWhatsAppEnabled = userConfig?.payload?.whatsapp_enabled;
  const {
    isUpdateAvailable,
    updateApp,
    dismissPopup,
    currentVersion,
    latestVersion,
  }: any = useVersionCheck(isAuthenticated);

  // Use the Produktly user identification hook
  useProduktlyUser();

  return (
    <ThemeProvider theme={theme}>
      {isAuthenticated && (
        <AnimatedUpdateNotification
          isUpdateAvailable={isUpdateAvailable}
          currentVersion={currentVersion}
          latestVersion={latestVersion}
          updateApp={updateApp}
          dismissPopup={dismissPopup}
        />
      )}
      {/* {isAuthenticated && !isWhatsAppEnabled && <AppDisabledFBM />} */}
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: localStoragePersister,
        dehydrateOptions: {
          shouldDehydrateQuery: (query: any) =>
            defaultShouldDehydrateQuery(query) && query.meta?.persist === true,
        },
        maxAge: 1000 * 60 * 10, // 10 minutes
      }}
    >
      <Provider store={store}>
        <ThemedApp />
        <AppContent />
      </Provider>
    </PersistQueryClientProvider>
  );
};

export default App;
